// src/lib/render/function-graph-model.ts
//
// The pure math behind <FunctionGraph>: how each function kind is evaluated,
// which window is actually drawn, and which points are marked.
//
// It lives outside the component because two things must agree on it exactly:
// the SVG that a sighted student reads, and the text description a screen
// reader user hears (diagram-alt-text.ts). If the description computed its own
// window or its own default slope, it could describe a point the picture does
// not show — or miss the one it does.

export type FnKind = 'linear' | 'quadratic' | 'exponential' | 'cubic' | 'absolute'

export type FnSpec = {
  kind?: FnKind
  slope?: number
  intercept?: number
  a?: number
  b?: number
  c?: number
  d?: number
  base?: number
  h?: number
  k?: number
  color?: string
}

export type GraphPoint = { x: number; y: number; label?: string }

export type FunctionGraphParams = FnSpec & {
  xMin?: number
  xMax?: number
  yMin?: number
  yMax?: number
  highlightX?: number
  points?: GraphPoint[]
  showRiseRun?: boolean
  curves?: FnSpec[]
  shade?: 'above' | 'below'
  label?: string
}

export type GraphWindow = { xMin: number; xMax: number; yMin: number; yMax: number }

export const num = (v: unknown, d: number) => (typeof v === 'number' && Number.isFinite(v) ? v : d)

/** Evaluate a function spec at x (the renderer's defaults for missing coefficients). */
export function evalFn(s: FnSpec, x: number): number {
  switch (s.kind) {
    case 'quadratic':
      return num(s.a, 1) * x * x + num(s.b, 0) * x + num(s.c, 0)
    case 'exponential':
      return num(s.a, 1) * Math.pow(num(s.base, 2), x) + num(s.c, 0)
    case 'cubic':
      return num(s.a, 1) * x ** 3 + num(s.b, 0) * x * x + num(s.c, 0) * x + num(s.d, 0)
    case 'absolute':
      return num(s.a, 1) * Math.abs(x - num(s.h, 0)) + num(s.k, 0)
    case 'linear':
    default:
      return num(s.slope, 1) * x + num(s.intercept, 0)
  }
}

/** The turning point of a single-extremum curve (parabola vertex / absolute-value
 *  cusp), or null for kinds without a simple one. opensUp=true → it's a minimum. */
export function vertexExtremum(p: FnSpec): { x: number; y: number; opensUp: boolean } | null {
  if (p.kind === 'quadratic') {
    const a = num(p.a, 1)
    if (a === 0) return null
    const x = -num(p.b, 0) / (2 * a)
    return { x, y: evalFn(p, x), opensUp: a > 0 }
  }
  if (p.kind === 'absolute') {
    const a = num(p.a, 1)
    if (a === 0) return null
    return { x: num(p.h, 0), y: num(p.k, 0), opensUp: a > 0 }
  }
  return null
}

/**
 * The window the graph is drawn in. Auto-frames the vertical range so a
 * single-extremum curve's turning point (parabola vertex / V cusp) stays
 * ON-canvas when the author didn't pin yMin/yMax. We only EXTEND toward the
 * vertex — never inflate from the (possibly steep) arms — so well-framed graphs
 * are unchanged and steep curves simply let their arms clip at the existing
 * top/bottom. Fixes vertices rendering off-canvas, e.g. f(x)=2x²−4x−6 whose
 * vertex (1,−8) fell ~51px below the frame.
 */
export function graphWindow(p: FunctionGraphParams): GraphWindow {
  const xMin = num(p.xMin, -2)
  const xMax = num(p.xMax, 8)
  const yMinSet = typeof p.yMin === 'number' && Number.isFinite(p.yMin)
  const yMaxSet = typeof p.yMax === 'number' && Number.isFinite(p.yMax)
  let yMin = num(p.yMin, -2)
  let yMax = num(p.yMax, 12)
  const vertex = vertexExtremum(p)
  if (vertex && Number.isFinite(vertex.y)) {
    if (vertex.opensUp && !yMinSet && vertex.y < yMin) yMin = Math.floor(vertex.y - 1.5)
    if (!vertex.opensUp && !yMaxSet && vertex.y > yMax) yMax = Math.ceil(vertex.y + 1.5)
  }
  return { xMin, xMax, yMin, yMax }
}

/** The overlay curves (systems of equations), as the renderer reads them. */
export function overlayCurves(p: FunctionGraphParams): FnSpec[] {
  return Array.isArray(p.curves) ? p.curves.filter((c) => !!c && typeof c === 'object') : []
}

/**
 * Decide which set of points to highlight: explicit `points` wins; otherwise
 * `highlightX` computes the matching y on the primary curve (legacy lessons);
 * else none.
 */
export function resolveHighlightPoints(p: FunctionGraphParams): GraphPoint[] {
  if (Array.isArray(p.points) && p.points.length > 0) {
    return p.points.filter(
      (pt): pt is GraphPoint => typeof pt?.x === 'number' && typeof pt?.y === 'number',
    )
  }
  if (typeof p.highlightX === 'number') {
    const x = p.highlightX
    const y = evalFn(p, x)
    return [{ x, y, label: `(${x}, ${formatNum(y)})` }]
  }
  return []
}

/** The rise/run annotation the renderer draws, or null when it draws none. */
export function riseRunAnnotation(
  p: FunctionGraphParams,
): { from: GraphPoint; to: GraphPoint; rise: number; run: number } | null {
  const pts = resolveHighlightPoints(p)
  if (p.showRiseRun !== true || (p.kind ?? 'linear') !== 'linear' || pts.length < 2) return null
  return { from: pts[0], to: pts[1], rise: pts[1].y - pts[0].y, run: pts[1].x - pts[0].x }
}

export function formatNum(n: number): string {
  if (Number.isInteger(n)) return String(n)
  return n.toFixed(2).replace(/\.?0+$/, '')
}
